import '/assets/styles/pages/payment.scss';
import { Trans } from 'react-i18next';
import { useEffect, useState, useContext } from 'react';
import classNames from 'classnames';

// Context
import { useBranchSelection } from '/utilities/branch-selection';
import { useBasket } from '/controllers/basket';
import { useNavigator, useParams } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { FormContext } from '@wearetla/tla-essentials-tools/partials/forms/context/form-context';
import { useStorage } from '@wearetla/tla-essentials-tools/utilities/storage';
import { useTranslation } from '/utilities/i18n';

// Sections
import PageHeader from '/views/sections/page-header';
import BasketControls from '/views/sections/basket-controls';

// Partials
import Icon from '@wearetla/tla-essentials-tools/partials/icon';
import Img from '@wearetla/tla-essentials-tools/partials/img';
import Btn from '/views/partials/btn';
import Loader from '/views/partials/loader';
import EmptyMessage from '/views/partials/empty-message';
import Pricebox from '/views/partials/pricebox';
import CheckoutSection from '/views/partials/checkout-section';
import { Form, Input, FormFeedback } from '/views/partials/forms';

// Services
import orderServices from '/services/order';
import paymentServices from '/services/payment';

const Payment = () => {
	const { orderId } = useParams('payment');
	const {
		selectedBranch,
		branchTableTitle,
		initialized: branchSelectionInitialized,
	} = useBranchSelection();
	const { openModal } = useModals();
	const { redirect } = useNavigator();
	const { t } = useTranslation();
	const { lastCardSelection, setData: setStorageData } = useStorage();
	const { clearBasket } = useBasket();

	const [fetchingOrder, setFetchingOrder] = useState(false);
	const [cards, setCards] = useState(null);
	const [selectedCard, setSelectedCard] = useState(null);
	const [selectedDelivery, setSelectedDelivery] = useState(null);
	const [sale, setSale] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [feedbackData, setFeedbackData] = useState(null);

	const getSavedCards = () => {
		return new Promise((resolve, reject) => {
			paymentServices
				.getSavedCards()
				.then((cardsPayload) => {
					setCards(cardsPayload);
					resolve(cardsPayload);
				})
				.catch((error) => {
					setCards([]);
					reject(error);
				});
		});
	};

	const getOrder = (reset = false) => {
		if (reset) {
			setSale(null);
		}

		setFetchingOrder(true);

		orderServices.getOrder(orderId).then((payload) => {
			if (payload.status === 0) {
				if (payload.delivery_type !== undefined) {
					setSelectedDelivery(
						payload.delivery_types.find((type) => type.value === payload.delivery_type?.id) ?? null
					);
				} else {
					setSelectedDelivery(null);
				}
				setSale(payload);
				setFetchingOrder(false);
			} else if (payload.status === 1) {
				redirect('orderSummary', { orderId: payload.id });
			} else {
				redirect('basket');
			}
		});
	};

	useEffect(() => {
		if (!sale && branchSelectionInitialized && selectedBranch && orderId) {
			getOrder();
		} else if (orderId && sale && sale?.id?.toString?.() !== orderId) {
			setSale(null);
		} else if (branchSelectionInitialized && !selectedBranch) {
			redirect('basket');
		}
	}, [selectedBranch, branchSelectionInitialized, sale, orderId]);

	useEffect(() => {
		if (lastCardSelection && cards?.length) {
			const foundCard = cards.find((card) => card.card_key === lastCardSelection);
			if (foundCard) {
				setSelectedCard(foundCard);
			}
		}
	}, [cards, lastCardSelection]);

	useEffect(() => {
		if (selectedCard && selectedCard?.card_key !== lastCardSelection) {
			setStorageData('lastCardSelection', selectedCard.card_key);
		}
	}, [lastCardSelection, selectedCard]);

	useEffect(() => {
		getSavedCards();
	}, []);

	useEffect(() => {
		if (sale && selectedDelivery && sale?.delivery_type?.id !== selectedDelivery?.value) {
			setFetchingOrder(true);
			orderServices.updateOrderDelivery(sale.id, selectedDelivery.value).then(() => {
				getOrder();
			});
		}
	}, [selectedDelivery, sale]);

	const openCardSelection = () => {
		openModal('paymentCardSelection', {
			cards,
			selectedCard,
			onRefetch: getSavedCards,
			onSelect: (selectedCard) => {
				setSelectedCard(selectedCard);
			},
		});
	};

	const submit = () => {
		setSubmitting(true);
		paymentServices
			.makePayment(
				sale.id,
				{
					holder_name: selectedCard.holder_name ?? t('Kayıtlı Kart'),
					card_number: selectedCard?.card_number ?? '5555444433331111',
					expire_month: selectedCard?.expire_month ?? '12',
					expire_year: selectedCard?.expire_year ?? '40',
					cvv: selectedCard?.cvv ?? '111',
					card_register: selectedCard?.card_register || false,
					card_uid: selectedCard?.uuid ?? null,
				},
				{ openModal }
			)
			.then(() => {
				clearBasket();
				redirect('orderSummary', { orderId: sale.id });
			})
			.catch((feedback) => {
				setFeedbackData(feedback);
				setSubmitting(false);
			});
	};

	return (
		<Form onSubmit={submit}>
			<PageHeader icon="menu" backLink="home" title={t('Siparişim')} subtitle={branchTableTitle} />

			<div className="payment-wrap loader-container">
				<Loader dark loading={!sale} />
				<FormFeedback className="payment-feedback" data={feedbackData} displayFormValidation />
				<Input
					type="hidden"
					hideError
					validation={{
						required: t('Ödeme yapacağın kartı seçmelisin.'),
					}}
					name="selected_card"
					value={selectedCard?.value}
				/>
				{sale && (
					<>
						<CheckoutSection narrow title={t('Servis Yöntemi')} className="payment-delivery">
							{!!sale.delivery_types && (
								<Input
									className="delivery-types square"
									name="delivery_type"
									type="radio"
									value={selectedDelivery}
									disabled={fetchingOrder}
									onChange={setSelectedDelivery}
									options={sale.delivery_types?.map((type) => ({
										...type,
										label: (
											<div className="types-type">
												<Icon
													name={type.id === 0 ? 'service-pickup' : 'service-table'}
													className="type-icon"
												/>
												<div className="type-textcontent">
													<strong className="type-title">{type.title}</strong>
													<div className="type-price">
														{type.price > 0 ? '+' : ''}
														<Pricebox horizontal price={type.price} />
													</div>
												</div>
											</div>
										),
									}))}
									validation={{
											required: t('Servis yöntemi seçmelisin.'),
									}}
								/>
							)}
						</CheckoutSection>
						<CardsWrap selectedCard={selectedCard}>
							<Loader loading={!cards} />
							{!(cards?.length || selectedCard) ? (
								<>
									<EmptyMessage>{t('Kayıtlı Kartın Bulunmuyor.')}</EmptyMessage>
									<Btn
										disabled={fetchingOrder}
										className="primary block"
										onClick={openCardSelection}
									>
										{t('Kart Ekle')}
									</Btn>
								</>
							) : (
								<>
									{selectedCard && (
										<div className="card-preview">
											<div className="preview-cardsummary">
												<Img className="cardsummary-typeimage" src={selectedCard.typeImage} />
												<div className="cardsummary-textarea">
													<strong className="cardsummary-title">
														{selectedCard.card_platform}
													</strong>
													<span className="cardsummary-number">
														{selectedCard.card_masked_number}
													</span>
												</div>

												<Btn onClick={openCardSelection} className="primary cardsummary-editbtn">
													{t('Değiştir')}
												</Btn>
											</div>
											<span className="preview-message">{t('Ödemen bu kart ile yapılacak.')}</span>
										</div>
									)}
									{!!cards.length && !selectedCard && (
										<div className="card-selectmessage">
											<span>
												<strong>{cards.length}</strong> {t('kayıtlı kartın bulunuyor.')}
											</span>
											<Btn disabled={fetchingOrder} className="primary" onClick={openCardSelection}>
												{t('Kart Seç')}
											</Btn>
										</div>
									)}
								</>
							)}
						</CardsWrap>
						{/* <CheckoutSection narrow title="Not" className="payment-note">
							<Input disabled={fetchingOrder} type="textarea" className="note" />
						</CheckoutSection> */}
						<CheckoutSection
							narrow
							title={t('Ödeme Özeti')}
							contentClassName="sum-container"
							className="payment-sum"
						>
							<div className="sum-item">
								<strong>{t('Ürünler Toplamı')}</strong>
								<Pricebox horizontal price={sale.product_total_price} />
							</div>
							{(sale.extras ?? []).map((extra) => (
								<div className="sum-item" key={extra.id}>
									<strong>{extra.title}</strong>
									<Pricebox horizontal price={extra.price} />
								</div>
							))}
							<div className="sum-item highlight">
								<strong>{t('Toplam')}</strong>
								<Pricebox horizontal price={sale.total_price} />
							</div>
							<Input
								type="checkbox"
								name="agreement"
								className="sum-agreement"
								disabled={fetchingOrder}
								validation={{
									required: t('Mesafeli satış sözleşmesini kabul etmelisin.'),
								}}
								label={
									<Trans
										i18nKey="<0>{{title}}</0> içeriğini okudum ve kabul ediyorum."
										values={{ title: t('Mesafeli satış sözleşmesi') }}
										components={[
											<button
												key="sale-term"
												type="button"
												className="hyperlink"
												onClick={() => {
													openModal('agreement', { agreement: 'SALE_TERM' });
												}}
											/>,
										]}
									/>
								}
							/>
						</CheckoutSection>
					</>
				)}
			</div>

			<BasketControls
				total={sale?.total_price ?? false}
				disabled={fetchingOrder}
				loading={submitting}
			/>
		</Form>
	);
};

const CardsWrap = ({ children, selectedCard }) => {
	const { touched } = useContext(FormContext);
	const { t } = useTranslation();

	return (
		<CheckoutSection
			narrow
			title={t('Ödeme Yöntemi')}
			contentClassName="card-contentwrap low loader-container"
			className={classNames('payment-card', { error: touched && !selectedCard })}
		>
			{children}
		</CheckoutSection>
	);
};

export default Payment;
